import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { clientAuthService, ForgotPasswordDto } from '@/services/client-auth.service';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, useToast } from '@caruso/ui';

export function ClientForgotPassword() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const forgotPasswordMutation = useMutation({
    mutationFn: (data: ForgotPasswordDto) => clientAuthService.forgotPassword(data),
    onSuccess: (data) => {
      setEmailSent(true);
      toast({
        title: 'Email enviado!',
        description: data.message,
        variant: 'success',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao enviar email',
        description: error.response?.data?.message || 'Ocorreu um erro ao processar sua solicitação',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({
        title: 'Campo obrigatório',
        description: 'Por favor, preencha seu email',
        variant: 'destructive',
      });
      return;
    }

    forgotPasswordMutation.mutate({ email });
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Card className="bg-neutral-900/50 backdrop-blur border-neutral-800">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-brand-gold/10 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-brand-gold" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Email Enviado!</h2>
                <p className="text-neutral-400 mb-6">
                  Se existe uma conta associada ao email <strong className="text-brand-gold">{email}</strong>,
                  você receberá instruções para redefinir sua senha.
                </p>
                <p className="text-sm text-neutral-500 mb-6">
                  Não se esqueça de verificar sua caixa de spam.
                </p>
                <Button
                  onClick={() => navigate('/client/login')}
                  className="w-full bg-brand-gold hover:bg-brand-gold/90 text-black font-semibold"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar para o Login
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="bg-neutral-900/50 backdrop-blur border-neutral-800">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-brand-gold/10 rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 text-brand-gold" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-white">Recuperar Senha</CardTitle>
            <CardDescription className="text-neutral-400">
              Digite seu email para receber um link de recuperação de senha
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-300">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <Input
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-neutral-800/50 border-neutral-700 text-white placeholder:text-neutral-500"
                    disabled={forgotPasswordMutation.isPending}
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-brand-gold hover:bg-brand-gold/90 text-black font-semibold"
                disabled={forgotPasswordMutation.isPending}
              >
                {forgotPasswordMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Enviar Link de Recuperação
                  </>
                )}
              </Button>

              <div className="text-center text-sm text-neutral-400 mt-4">
                Lembrou sua senha?{' '}
                <Link
                  to="/client/login"
                  className="text-brand-gold hover:text-brand-gold/80 font-medium"
                >
                  Fazer login
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Link
            to="/"
            className="text-sm text-neutral-500 hover:text-neutral-400"
          >
            Voltar para o site
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
